import { Check } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { Field } from '../../../components/Field';
import { Textarea } from '../../../components/Input';
import { SkeletonLines } from '../../../components/Skeleton';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { useRecordWithdrawal, useWithdrawalReasons } from '../api/candidates-api';
import styles from './WithdrawalPicker.module.css';

/**
 * Appendix A "any active → withdrawn — reason captured; NOT a rejection", recorded on the
 * candidate's behalf (DECISIONS-LOG S29–S32 Q4). §L20's reasons, one to pick; a note if useful.
 * The two that mean the process failed are marked, because choosing them tells the HR Lead.
 */
export function WithdrawalPicker({
  applicationId,
  version,
  candidateName,
  onDone,
  onConfirm,
}: {
  readonly applicationId: string;
  readonly version: number;
  readonly candidateName: string;
  readonly onDone: () => void;
  /** R7: the contact tracker records the withdrawal with the contact status (§L32), so it takes the choice itself. */
  readonly onConfirm?: (choice: { readonly reasonCode: string; readonly note: string }) => void;
}): ReactNode {
  const reasons = useWithdrawalReasons(true);
  const record = useRecordWithdrawal();
  const showToast = useToasts((state) => state.show);
  const [reasonCode, setReasonCode] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const firstRef = useRef<HTMLButtonElement | null>(null);

  // The panel is positioned on its first frame; focus goes in on the next (as the reason picker).
  const hasReasons = reasons.data !== undefined;
  useEffect(() => {
    if (!hasReasons) return;
    const frame = requestAnimationFrame(() => firstRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [hasReasons]);

  const chosen = reasons.data?.find((reason) => reason.code === reasonCode) ?? null;
  const confirm = (): void => {
    if (!chosen) return;
    onDone();
    if (onConfirm) {
      onConfirm({ reasonCode: chosen.code, note: note.trim() });
      return;
    }
    record.mutate(
      { applicationId, reasonCode: chosen.code, version, ...(note.trim() ? { note: note.trim() } : {}) },
      {
        onSuccess: () => showToast({ tone: 'success', message: `${candidateName} withdrew`, detail: chosen.isAccountability ? `${chosen.label} — HR has been told.` : chosen.label }),
        onError: (error) => showToast({ tone: 'danger', message: `${candidateName} is still in the process`, detail: errorMessage(error) }),
      },
    );
  };

  return (
    <div
      className={styles.picker}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          confirm();
        }
      }}
    >
      <p className={styles.title}>
        Record that {candidateName} withdrew
        <span className={styles.subtitle}>The reason they gave. This is not a rejection, and they get no regret letter.</span>
      </p>
      {!reasons.data ? (
        <SkeletonLines count={4} />
      ) : (
        <ul className={styles.reasons} role="radiogroup" aria-label="Reason the candidate gave">
          {reasons.data.map((reason, index) => {
            const isChosen = reason.code === reasonCode;
            return (
              <li key={reason.code}>
                <button ref={index === 0 ? firstRef : undefined} type="button" role="radio" aria-checked={isChosen} className={isChosen ? styles.reasonChosen : styles.reason} onClick={() => setReasonCode(reason.code)}>
                  <span className={styles.reasonLabel}>{reason.label}</span>
                  {reason.isAccountability && <span className={styles.tag}>Tells HR</span>}
                  {isChosen && <Check size={14} aria-hidden />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <Field label="Note" isOptional>
        {({ inputId }) => <Textarea id={inputId} rows={2} maxLength={2000} value={note} placeholder="What they said, if it helps" onChange={(event) => setNote(event.target.value)} />}
      </Field>
      <div className={styles.footer}>
        <span className={styles.subtitle}>{chosen ? 'Ctrl+Enter records it' : 'Pick the reason they gave.'}</span>
        <Button variant="solid" size="sm" disabled={!chosen} onClick={confirm}>
          Record withdrawal
        </Button>
      </div>
    </div>
  );
}
